/**
 * The Meta Pixel and, since 2026-10-04, the Google Ads tag on the registration pages only (issue #15,
 * 2026-09-27), never inside the app.
 *
 * Loaded only on app.timefly.no, only when the visitor gave marketing consent on timefly.no (the
 * tf_consent cookie on .timefly.no, written by the site's consent box), and never in tests or locally,
 * so nothing but real production visits reaches the ad account. Automatic event detection and
 * advanced matching stay off; no name, address or id is sent.
 *
 *   PageView              Registrer, when it opens
 *   CompleteRegistration  Registrer's form accepted by the server (signup or demo link), once a page
 *                         load. It was the first confirmation of the address, but on a phone that link
 *                         opens in another browser than the ad's, where there is no consent cookie and
 *                         no ad click to tie it to: 9 visits from the ad and 4 demo clicks in PostHog
 *                         (28-30.09) against 0 registrations in Ads Manager (2026-09-30)
 *
 * Google Ads (2026-10-04) gets its Sign-up conversion at the same moment as CompleteRegistration, and
 * ties it to the ad click by the _gcl_aw cookie the site's tag set on .timefly.no (consent.js).
 */
const PIXEL_ID = '1317310373692924';
// Google Ads account 219-108-7299: the tag, and its Sign-up conversion (Goals > Conversions)
const GADS_ID = 'AW-18434745759';
const GADS_SIGNUP = `${GADS_ID}/Usa9CI245ZAdEJ_Lr9ZE`;

type Fbq = ((...a: unknown[]) => void) & {
  callMethod?: (...a: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

export function marketingConsent(): boolean {
  return /(?:^|;\s*)tf_consent=v1\.a[01]\.m1/.test(document.cookie);
}

/** An explicit no to marketing in the cookie box (tf_consent=…m0). */
const marketingRefused = () => /(?:^|;\s*)tf_consent=v1\.a[01]\.m0/.test(document.cookie);

/**
 * TEMPORARY (owner, 2026-10-04, to test the ad flow): the Meta pixel and the Google Ads tag run before any
 * choice in the cookie box and stop only on an explicit no to marketing. Against ekomloven §3-15; restore
 * by replacing `!marketingRefused()` with `marketingConsent()` below, GitHub issue #41.
 */
function pixelEnabled(): boolean {
  return import.meta.env.MODE !== 'test' && location.hostname === 'app.timefly.no' && !marketingRefused();
}

function fbq(): Fbq | null {
  if (!pixelEnabled()) return null;
  const w = window as unknown as { fbq?: Fbq; _fbq?: Fbq };
  if (!w.fbq) {
    const n: Fbq = Object.assign(
      (...a: unknown[]) => {
        n.callMethod ? n.callMethod(...a) : n.queue?.push(a);
      },
      { queue: [] as unknown[], loaded: true, version: '2.0' },
    );
    n.push = n;
    w.fbq = n;
    w._fbq = n;
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(s);
    n('set', 'autoConfig', false, PIXEL_ID);
    n('init', PIXEL_ID);
  }
  return w.fbq;
}

type Gtag = (...a: unknown[]) => void;

/** Google's gtag.js, as the site loads it (site/public/assets/js/consent.js loadGoogleAds). */
function gtag(): Gtag | null {
  if (!pixelEnabled()) return null;
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: Gtag };
  if (!w.gtag) {
    w.dataLayer = w.dataLayer || [];
    const layer = w.dataLayer;
    // gtag.js reads the arguments object itself; an array (rest parameters, or an arrow function, which
    // has no `arguments`) is ignored by it. Google's own snippet pushes `arguments` for this reason
    w.gtag = function gtagPush() {
      // biome-ignore lint/style/noArguments: gtag.js needs the arguments object, see above
      layer.push(arguments);
    };
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GADS_ID}`;
    document.head.appendChild(s);
    w.gtag('consent', 'default', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
    w.gtag('js', new Date());
    w.gtag('config', GADS_ID, { allow_ad_personalization_signals: false });
  }
  return w.gtag;
}

export function registrationPageView() {
  fbq()?.('track', 'PageView');
  gtag();
}

let registered = false;
export function completeRegistration() {
  if (registered || !pixelEnabled()) return;
  registered = true;
  fbq()?.('track', 'CompleteRegistration');
  gtag()?.('event', 'conversion', { send_to: GADS_SIGNUP });
}
